/**
 * CalibrationTakeEditForm — inline editor for a single calibration take's
 * lead-side declaration, failure-dimension tags, and score-band grade
 * (take-edit-frontend + grade-feel scoring plan Story 4).
 *
 * Closes the loop PR #393 opened: coach feedback now tells the coach to
 * "confirm that athlete's true lead side" when detection disagrees across
 * example videos — but shipped with no control anywhere in the product to
 * do it. This is that control.
 *
 * Opened from a per-row "Edit" button in CalibrationTakeList.tsx. Three
 * independent fields, any subset of which may be saved:
 *
 *  - Lead side: Auto-detected (default) | Left | Right. A declared value
 *    WINS over detection (the backend's single mirroring decision point —
 *    see CLAUDE.md § Mirroring). Whenever a detected value exists, it's
 *    shown alongside the picker — and called out explicitly when it
 *    disagrees with the chosen value — because that disagreement is the
 *    entire reason this control exists; burying it would defeat the point.
 *
 *  - Failure dimensions: multi-select tags, offered only for `bad`/`fail`
 *    takes (the API 422s a `good` take carrying this field — a `good`
 *    take has nothing to scope a mistake to). Options come from
 *    utils/constants.ts's CALIBRATION_REAL_FAILURE_DIMENSION_OPTIONS — the
 *    same source CalibrationTakeUploader's upload-time dropdown reads —
 *    not a second hardcoded list. Two independently-maintained
 *    vocabularies is exactly the class of drift that produced a silent
 *    zero-rules bug in this repo (2026-08-09 rule-compiler stitch).
 *
 *  - Grade (score band): a 0-100 NUMBER (numeric take-grade entry — was a
 *    letter A-F dropdown), offered on a take of ANY label (owner
 *    requirement — a SEPARATE control from failure dimensions, no label
 *    restriction). Since the Story-5 flip the backend DOES re-trigger
 *    calibration for a grade-only save (grade_numeric/grade_label are in
 *    calibration_take_routes.py's `_measurement_fields_touched`: grades
 *    are read by both grade-constrained pricing and the grade-scale mint)
 *    — same as lead side / failure dimensions below. LEGACY-PREFILL RULE:
 *    a take graded only with the old letter (`grade_label` set,
 *    `grade_numeric` null) shows that letter as a read-only chip and
 *    leaves the number input EMPTY (placeholder shows the band's
 *    representative value) — we never silently convert a coach's "B" into
 *    a fake-precision "85" just because they saved something else on this
 *    take. Only typing an actual number sends `grade_numeric`.
 *
 * Save sends only the field(s) that actually changed (the Save button is
 * disabled otherwise). Saving any of the three fields re-triggers
 * calibration server-side regardless of whether the value differs (a
 * no-op save would still cost a recalibration pass for nothing, which is
 * exactly why only-changed fields are sent).
 *
 * Copy rules (CLAUDE.md): coach language only — never surface A_orientation
 * / Kc / Ks / z-scores. No scoring-effect claims ("will score zero", "loses
 * points") — whether a boundary affects scoring depends on env flags this
 * client cannot see. Copy here states what the setting *means* (which side
 * leads; what a tag scopes; what a grade teaches), not what it will do to
 * a score.
 */

import { useState } from 'react';
import { MAX_REASON_TEXT_LENGTH } from './CalibrationTakeUploader';
import AthleteVisibilityControl from './AthleteVisibilityControl';
import { Button, Alert, Spinner, Checkbox } from '@/components/ui';
import { useUpdateCalibrationTake } from '@/hooks/useCalibrationTakes';
import { CALIBRATION_REAL_FAILURE_DIMENSION_OPTIONS } from '@/utils/constants';
import {
  GRADE_LABEL_MIDPOINTS,
  GRADE_NUMERIC_MAX,
  GRADE_NUMERIC_MIN,
  gradeNumericToDisplayGrade,
  isValidGradeNumeric,
  parseGradeNumericInput,
} from '@/utils/gradeNumeric';
import { getErrorMessage } from '@/utils/errors';
import type {
  CalibrationTake,
  CalibrationTakeUpdateRequest, CalibrationTakeLabel } from '@/types/calibrationTake';

type LeadSideChoice = 'auto' | 'left' | 'right';

function toChoice(declared: string | null | undefined): LeadSideChoice {
  return declared === 'left' || declared === 'right' ? declared : 'auto';
}

function formatSide(side: string | null | undefined): string | null {
  if (side === 'left') return 'Left';
  if (side === 'right') return 'Right';
  return null;
}

/** Order-insensitive set comparison for the dimension tag list. */
function sameDimensions(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
}

interface CalibrationTakeEditFormProps {
  templateId: string;
  take: CalibrationTake;
  onClose: () => void;
}

export default function CalibrationTakeEditForm({
  templateId,
  take,
  onClose,
}: CalibrationTakeEditFormProps) {
  const updateMutation = useUpdateCalibrationTake(templateId);

  const [leadSide, setLeadSide] = useState<LeadSideChoice>(toChoice(take.declared_lead_side));

  const initialDimensions =
    take.failure_dimensions && take.failure_dimensions.length > 0
      ? take.failure_dimensions
      : take.failure_dimension
        ? [take.failure_dimension]
        : [];
  const [dimensions, setDimensions] = useState<string[]>(initialDimensions);
  // LEGACY-PREFILL RULE (see module docstring): prefill with the actual
  // numeric grade when one is set; otherwise leave EMPTY even when a
  // legacy grade_label exists — never synthesize a fake-precision number
  // from a letter. The legacy letter (if any) still shows as a chip below.
  const [gradeNumericInput, setGradeNumericInput] = useState<string>(
    take.grade_numeric != null ? String(take.grade_numeric) : '',
  );
  const [reasonText, setReasonText] = useState<string>(take.reason_text ?? '');
  const [label, setLabel] = useState<CalibrationTakeLabel>(take.label);
  const [error, setError] = useState<string | null>(null);

  // Follows the EDITED label, not the saved one: a coach switching a take to
  // Correct should immediately stop being offered mistake tags for it.
  const canScopeDimensions = label === 'bad' || label === 'fail';

  const detectedLabel = formatSide(take.detected_lead_side);
  const chosenDeclared: string | null = leadSide === 'auto' ? null : leadSide;
  const disagreesWithDetected =
    chosenDeclared !== null &&
    take.detected_lead_side != null &&
    chosenDeclared !== take.detected_lead_side;

  const originalDeclared = take.declared_lead_side ?? null;
  const leadSideDirty = chosenDeclared !== originalDeclared;
  const dimensionsDirty = canScopeDimensions && !sameDimensions(dimensions, initialDimensions);

  // Grade (numeric take-grade entry) — dirty iff the PARSED value differs
  // from the take's saved grade_numeric (null when the input is empty or
  // unparseable-as-typed-so-far; see parseGradeNumericInput). A legacy
  // letter-only take (grade_numeric null, grade_label set) starts with an
  // empty input, so leaving it empty is correctly "not dirty" — the whole
  // point of the legacy-prefill rule (module docstring).
  const parsedGradeNumeric = parseGradeNumericInput(gradeNumericInput);
  const originalGradeNumeric = take.grade_numeric ?? null;
  const gradeNumericDirty = parsedGradeNumeric !== originalGradeNumeric;

  const reasonDirty = reasonText.trim() !== (take.reason_text ?? '').trim();
  const labelDirty = label !== take.label;
  const isDirty =
    leadSideDirty || dimensionsDirty || gradeNumericDirty || reasonDirty || labelDirty;

  // Live letter-grade chip (display only, matches CalibrationTakeUploader's
  // pattern) — shown while the typed value is a valid in-range number.
  const gradeChipLetter =
    parsedGradeNumeric !== null && isValidGradeNumeric(parsedGradeNumeric)
      ? gradeNumericToDisplayGrade(parsedGradeNumeric)
      : null;

  // Legacy-prefill support: a take with a saved letter but no numeric grade
  // yet — show the letter as a read-only fact and hint the band's
  // representative value as the input's placeholder (GRADE_LABEL_MIDPOINTS
  // mirrors the backend's BAND_MIDPOINTS; display-only, never submitted).
  const legacyLetterOnly = take.grade_numeric == null && take.grade_label != null;
  const gradeNumericPlaceholder = legacyLetterOnly
    ? String(GRADE_LABEL_MIDPOINTS[take.grade_label!])
    : 'e.g. 85';

  const toggleDimension = (value: string) => {
    setDimensions((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleSave = async () => {
    if (!isDirty) {
      onClose();
      return;
    }
    if (parsedGradeNumeric !== null && !isValidGradeNumeric(parsedGradeNumeric)) {
      setError(`Grade must be a number between ${GRADE_NUMERIC_MIN} and ${GRADE_NUMERIC_MAX}.`);
      return;
    }
    setError(null);
    const updates: CalibrationTakeUpdateRequest = {};
    if (leadSideDirty) {
      updates.declared_lead_side = chosenDeclared;
    }
    if (dimensionsDirty) {
      updates.failure_dimensions = dimensions.length > 0 ? dimensions : null;
    }
    if (gradeNumericDirty) {
      updates.grade_numeric = parsedGradeNumeric;
    }
    if (reasonDirty) {
      // Empty clears the field, matching the upload contract.
      updates.reason_text = reasonText.trim() || null;
    }
    if (labelDirty) {
      updates.label = label;
      // A take moving to Correct has no failure to describe. The backend
      // clears the tags too; sending it explicitly keeps the request
      // self-describing rather than relying on a server-side side effect.
      if (label === 'good' && dimensions.length > 0) {
        updates.failure_dimensions = null;
      }
    }
    try {
      await updateMutation.mutateAsync({ takeId: take.take_id, updates });
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save changes. Please try again.'));
    }
  };

  return (
    <div
      data-testid={`edit-form-${take.take_id}`}
      className="mt-2 space-y-3 rounded-lg border border-border-subtle bg-dark-elevated-2 p-3"
    >
      {/* Label (which evidence pool this take belongs to) — Wave 2.
          Editable in place because a mis-click previously required delete +
          re-upload, which also destroyed the archived video, the pose cache
          and the take's measured score. */}
      <div>
        <p className="text-xs font-medium text-text-secondary mb-1.5">This example is</p>
        <div className="flex gap-1.5">
          {([
            { value: 'good', label: 'Correct' },
            { value: 'bad', label: 'Flawed' },
            { value: 'fail', label: 'Disqualifying' },
          ] as const).map((choice) => (
            <button
              key={choice.value}
              type="button"
              data-testid={`edit-label-${choice.value}`}
              onClick={() => setLabel(choice.value)}
              aria-pressed={label === choice.value}
              className={`flex-1 px-2 py-1.5 rounded border text-xs font-medium transition-colors ${
                label === choice.value
                  ? 'bg-accent/10 border-accent/50 text-accent'
                  : 'bg-dark-elevated-1 border-border-subtle text-text-secondary hover:text-text-primary'
              }`}
            >
              {choice.label}
            </button>
          ))}
        </div>
        {labelDirty && label === 'good' && dimensions.length > 0 && (
          <p className="text-xs text-text-muted mt-1.5">
            The mistake tags on this example will be removed — a correct
            example has no mistake to describe.
          </p>
        )}
      </div>

      {/* Reason text — the coach's own words. This is what the rule compiler
          reads, so refining wording is a first-class workflow. */}
      <div>
        <label
          htmlFor={`reason-text-${take.take_id}`}
          className="text-xs font-medium text-text-secondary mb-1.5 block"
        >
          Why is this example {label === 'good' ? 'correct' : label === 'bad' ? 'flawed' : 'disqualifying'}?{' '}
          <span className="text-text-muted font-normal">(optional)</span>
        </label>
        <textarea
          id={`reason-text-${take.take_id}`}
          data-testid="edit-reason-text"
          value={reasonText}
          onChange={(e) => setReasonText(e.target.value.slice(0, MAX_REASON_TEXT_LENGTH))}
          rows={3}
          className="w-full px-2 py-1.5 border border-border-subtle rounded bg-dark-elevated-1
            text-text-primary text-xs focus:outline-none focus:ring-1 focus:ring-accent resize-y"
        />
        {reasonText.length > MAX_REASON_TEXT_LENGTH - 200 && (
          <p className="text-xs text-text-muted mt-1 text-right">
            {reasonText.length} / {MAX_REASON_TEXT_LENGTH}
          </p>
        )}
        <p className="text-xs text-text-muted mt-1.5">
          Your wording is read when turning these notes into measurable checks.
        </p>
      </div>

      {/* Lead side */}
      <div>
        <p className="text-xs font-medium text-text-secondary mb-1.5">Lead side</p>
        <div className="flex gap-1.5">
          {(['auto', 'left', 'right'] as const).map((choice) => (
            <button
              key={choice}
              type="button"
              data-testid={`lead-side-${choice}`}
              onClick={() => setLeadSide(choice)}
              aria-pressed={leadSide === choice}
              className={`flex-1 px-2 py-1.5 rounded border text-xs font-medium transition-colors ${
                leadSide === choice
                  ? 'bg-accent/10 border-accent/50 text-accent'
                  : 'bg-dark-elevated-1 border-border-subtle text-text-secondary hover:text-text-primary'
              }`}
            >
              {choice === 'auto' ? 'Auto-detected' : choice === 'left' ? 'Left' : 'Right'}
            </button>
          ))}
        </div>

        {/* Detection context — the disagreement is the whole point, so it's
            surfaced directly under the picker rather than left implicit. */}
        {detectedLabel ? (
          <p
            data-testid="lead-side-detection-note"
            className={`text-xs mt-1.5 ${disagreesWithDetected ? 'text-warning font-medium' : 'text-text-muted'}`}
          >
            {leadSide === 'auto'
              ? `Detection currently reads this take as ${detectedLabel}.`
              : disagreesWithDetected
                ? `This overrides detection — it read this take as ${detectedLabel}.`
                : `Matches what detection already read (${detectedLabel}).`}
          </p>
        ) : (
          <p className="text-xs text-text-muted mt-1.5">No detection result yet for this take.</p>
        )}
        <p className="text-xs text-text-muted mt-1">
          Which side leads the movement in this clip. Every left-right
          comparison drawn from this take depends on this being right.
        </p>
      </div>

      {/* Failure dimensions — bad/fail only; the API rejects this field on a good take. */}
      {canScopeDimensions ? (
        <div>
          <p className="text-xs font-medium text-text-secondary mb-1.5">
            What kind of mistake does this take show?
          </p>
          <div className="space-y-1">
            {CALIBRATION_REAL_FAILURE_DIMENSION_OPTIONS.map((opt) => (
              <Checkbox
                key={opt.value}
                label={opt.label}
                checked={dimensions.includes(opt.value)}
                onChange={() => toggleDimension(opt.value)}
              />
            ))}
          </div>
          <p className="text-xs text-text-muted mt-1.5">
            {dimensions.length === 0
              ? 'No tags selected — this take informs every dimension.'
              : "Tagging keeps this take's evidence to the mistakes you pick, instead of every dimension."}
          </p>
        </div>
      ) : (
        <p className="text-xs text-text-muted">
          Mistake tags are only for Flawed or Disqualifying takes.
        </p>
      )}

      {/* Grade (score band) numeric entry — numeric take-grade entry. A
          SEPARATE control from the failure-dimensions block above; offered
          on a take of ANY label. */}
      <div>
        <label
          htmlFor={`grade-numeric-${take.take_id}`}
          className="text-xs font-medium text-text-secondary mb-1.5 block"
        >
          Roughly what score band does this attempt deserve?{' '}
          <span className="text-text-muted font-normal">(optional)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            id={`grade-numeric-${take.take_id}`}
            data-testid="edit-grade-numeric"
            type="number"
            inputMode="numeric"
            min={GRADE_NUMERIC_MIN}
            max={GRADE_NUMERIC_MAX}
            step={1}
            value={gradeNumericInput}
            onChange={(e) => setGradeNumericInput(e.target.value)}
            placeholder={gradeNumericPlaceholder}
            className="w-24 px-2 py-1.5 border border-border-subtle rounded bg-dark-elevated-1
              text-text-primary text-xs focus:outline-none focus:ring-1 focus:ring-accent"
          />
          {gradeChipLetter && (
            <span
              data-testid="edit-grade-band-chip"
              className="text-xs font-medium px-2 py-1 rounded-full bg-dark-elevated-2 border border-border-subtle text-text-secondary"
            >
              Reads as {gradeChipLetter}
            </span>
          )}
        </div>
        {legacyLetterOnly && (
          <p
            data-testid="edit-grade-legacy-note"
            className="text-xs text-text-muted mt-1.5"
          >
            Currently graded {take.grade_label} (an older letter grade).
            Type a number to switch to the numeric scale — leaving this
            blank keeps the existing grade.
          </p>
        )}
        <p className="text-xs text-text-muted mt-1.5">
          Think of it as a school grade — 85 reads as a solid B. This
          teaches the scoring scale — it does not change how this take
          itself is scored.
        </p>
      </div>

      {/* Example Performances (backlog/example-performances-template-
          resources.md, PR 1+2) — owns its own immediate-effect mutation,
          independent of this form's batched isDirty/Save flow below. */}
      <AthleteVisibilityControl templateId={templateId} take={take} />

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          data-testid="save-take-edit"
          onClick={handleSave}
          disabled={updateMutation.isPending || !isDirty}
        >
          {updateMutation.isPending ? (
            <span className="flex items-center gap-1.5">
              <Spinner size="sm" />
              Saving...
            </span>
          ) : (
            'Save'
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="cancel-take-edit"
          onClick={onClose}
          disabled={updateMutation.isPending}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
